// Imperative state machine + animator for the speedrun progress bar. Lives
// outside React: owns its own rAF loop, writes clip-path directly to the
// bar element, and only notifies subscribers when commit/deficient state
// transitions actually flip. SpeedrunEngine wraps the gate and adds the
// celebration state machine on top; the React-side binding lives in
// useSpeedrunEngine.
//
// State per region:
//   displayed — animated 0..1, chases raw score at a tool-dependent rate.
//   committed — sticky once displayed reached 1.0. Never clears.
//   deficient — flips true when a committed region's raw score drops below
//               DETECTION_DEFICIENT_THRESHOLD, false again at raw >= 1.
//
// satisfied = committed && !deficient. The next-target picker prefers
// !committed first, then !satisfied, so heavy undos re-queue the region
// without yanking the player off their current target mid-stroke.
//
// Down direction: instant snap. If raw drops, displayed matches
// immediately so a stale-high bar can't commit through the player's
// correction.
import { DETECTION_DEFICIENT_THRESHOLD } from "../../constants";

const COMMIT_EPSILON = 0.001;

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export default class DetectionGate {
  constructor() {
    this._displayed = new Map();
    this._committed = new Map();
    this._deficient = new Map();
    this._regions = [];
    this._lastTool = "pen";
    this._fillMs = 250;
    this._strokeMs = 1100;
    this._barEl = null;
    this._barRegionId = null;
    this._listeners = new Set();
    this._raf = null;
    this._lastFrame = 0;
    this._step = this._step.bind(this);
  }

  // Called from React when scoring recomputes (or rates / tool change).
  // Synchronous: snaps displayed downward, runs deficiency, updates the
  // bar, kicks the rAF loop if anything still needs to animate up.
  update({ regions, lastTool, fillMs, strokeMs }) {
    this._regions = regions;
    this._lastTool = lastTool;
    this._fillMs = fillMs;
    this._strokeMs = strokeMs;

    let transitionsChanged = false;
    let needsLoop = false;

    for (const r of regions) {
      const target = clamp01(r.score ?? 0);
      const cur = this._displayed.get(r.id) ?? 0;
      if (cur > target) {
        this._displayed.set(r.id, target);
      } else if (cur < target) {
        needsLoop = true;
      }
      const next = this._displayed.get(r.id) ?? 0;
      if (this._maybeCommit(r.id, target, next)) transitionsChanged = true;
      if (this._updateDeficiency(r.id, target)) transitionsChanged = true;
    }

    this._writeBar();
    if (transitionsChanged) this._emit();
    if (needsLoop) this._startLoop();
  }

  // Called by ScorePanel whenever the bar element or the region it
  // displays changes. Idempotent — no-op when nothing changed.
  bindBar(el, regionId) {
    if (this._barEl === el && this._barRegionId === regionId) return;
    this._barEl = el;
    this._barRegionId = regionId;
    this._writeBar();
  }

  // For the panel's pip row + next-target selector. Reads-only snapshot.
  getRegionState(id) {
    const committed = !!this._committed.get(id);
    const deficient = !!this._deficient.get(id);
    return {
      committed,
      deficient,
      satisfied: committed && !deficient,
    };
  }

  subscribe(listener) {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  // Clears all per-region state but keeps the bar binding + listeners
  // so the gate can be reused for a fresh speedrun without rebinding.
  reset() {
    if (this._raf != null) cancelAnimationFrame(this._raf);
    this._raf = null;
    this._lastFrame = 0;
    this._displayed.clear();
    this._committed.clear();
    this._deficient.clear();
    this._regions = [];
    this._writeBar();
  }

  dispose() {
    if (this._raf != null) cancelAnimationFrame(this._raf);
    this._raf = null;
    this._listeners.clear();
    this._barEl = null;
  }

  _step(now) {
    this._raf = null;
    const dt = this._lastFrame ? now - this._lastFrame : 16;
    this._lastFrame = now;

    const rateMs = this._lastTool === "fill" ? this._fillMs : this._strokeMs;
    const ratePerMs = 1 / Math.max(1, rateMs);

    let transitionsChanged = false;
    let needsMore = false;

    for (const r of this._regions) {
      const target = clamp01(r.score ?? 0);
      const cur = this._displayed.get(r.id) ?? 0;
      if (cur >= target) continue;
      const next = Math.min(target, cur + ratePerMs * dt);
      this._displayed.set(r.id, next);
      if (next < target) needsMore = true;
      if (this._maybeCommit(r.id, target, next)) transitionsChanged = true;
    }

    this._writeBar();
    if (transitionsChanged) this._emit();
    if (needsMore) {
      this._raf = requestAnimationFrame(this._step);
    } else {
      this._lastFrame = 0;
    }
  }

  _startLoop() {
    if (this._raf != null) return;
    this._lastFrame = 0;
    this._raf = requestAnimationFrame(this._step);
  }

  _maybeCommit(id, target, displayed) {
    if (target < 1 - COMMIT_EPSILON) return false;
    if (displayed < 1 - COMMIT_EPSILON) return false;
    if (!this._committed.get(id)) {
      this._committed.set(id, true);
      this._deficient.set(id, false);
      return true;
    }
    if (this._deficient.get(id)) {
      this._deficient.set(id, false);
      return true;
    }
    return false;
  }

  _updateDeficiency(id, target) {
    if (!this._committed.get(id)) return false;
    const wasDeficient = !!this._deficient.get(id);
    let next = wasDeficient;
    if (!wasDeficient && target < DETECTION_DEFICIENT_THRESHOLD) {
      next = true;
    } else if (wasDeficient && target >= 1 - COMMIT_EPSILON) {
      next = false;
    }
    if (next !== wasDeficient) {
      this._deficient.set(id, next);
      return true;
    }
    return false;
  }

  _writeBar() {
    const el = this._barEl;
    if (!el) return;
    const id = this._barRegionId;
    if (id == null) return;
    const committed = !!this._committed.get(id);
    const deficient = !!this._deficient.get(id);
    const satisfied = committed && !deficient;
    const region = this._regions.find((r) => r.id === id);
    const overdrawn = !!region?.overdrawn;

    // Overdraw shows full bar (red, via CSS keyed on data-overdrawn)
    // even though raw score is low, so the player gets an unmistakable
    // "too much!" signal. Commit logic is unaffected — _maybeCommit
    // gates on the real target (region.score), which is well below 1
    // when overdrawn, so the gate won't false-commit through this.
    const displayed = overdrawn || satisfied ? 1 : this._displayed.get(id) ?? 0;
    const insetRight = (1 - displayed) * 100;
    el.style.clipPath = `inset(0 ${insetRight}% 0 0)`;

    // Stripes pause while the bar is still climbing toward raw target;
    // resume on stable / satisfied.
    const rawTarget = region ? clamp01(region.score ?? 0) : 0;
    const growing =
      !overdrawn && !satisfied && displayed < rawTarget - COMMIT_EPSILON;
    const flag = growing ? "1" : "0";
    if (el.dataset.growing !== flag) el.dataset.growing = flag;

    const overdrawFlag = overdrawn ? "1" : "0";
    if (el.dataset.overdrawn !== overdrawFlag)
      el.dataset.overdrawn = overdrawFlag;
  }

  _emit() {
    for (const l of this._listeners) l();
  }
}
